import { fn2nZ } from '../../../core/formatter/formatNumber';
import { getBasisjahr, transformValue } from './Energiekosten';
import { getEnergietraegerKeyMap } from '../../../main/functions/getEnergietraegerKeyMap';
import { IDs } from '../../../helpers/IDs';
import * as React from 'react';
import { renderTable } from './renderTable';
import { WHelpIcon } from '../../../core/elements/WHelpIcon';

export interface EnergiekostenAusgangsdatenProps {
    data: any;
    disabled?: boolean;
    onChange: (data: any) => void;
    selection: any;
}

export class EnergiekostenAusgangsdaten extends React.Component<EnergiekostenAusgangsdatenProps, {}> {

    private sumKosten: number = null;
    private sumVerbrauch: number = null;

    getData(key: string, callback: (data: any) => void): any[] {
        const energietraeger: any = (getEnergietraegerKeyMap() || {})[key] || {};
        const basisjahr: number = getBasisjahr(energietraeger, this.props.data);
        const kennzahlen: any = (this.props.data || {})[IDs.KENNZIFFERN_BERECHNET] || {};

        if (!basisjahr) {
            return [];
        }
        const kosten: number = kennzahlen[basisjahr][energietraeger.kKey];
        const verbrauch: number = kennzahlen[basisjahr][energietraeger.vKey];
        const preis: number = verbrauch > 0 ? kosten / verbrauch : null;

        if (typeof callback === 'function') {
            callback({ kosten, preis, verbrauch });
        }

        return [
            this.renderSelection(key),
            this.renderEnergietraeger(key),
            basisjahr,
            fn2nZ(verbrauch),
            fn2nZ(kosten),
            fn2nZ(preis),
        ];
    }

    getDataRows(): any {
        const energietraeger: any = getEnergietraegerKeyMap() || {};
        const tbody: any = { rows: [], rowStyles: [] };
        let sumKosten: number = null;
        let sumVerbrauch: number = null;

        Object.keys(energietraeger).forEach((key: string) => {
            const row: any[] = this.getData(
                key,
                (item: any) => {
                    sumKosten += transformValue(item.kosten, 2);
                    sumVerbrauch += transformValue(item.verbrauch, 2);
                }
            ) || [];

            if (row.length) {
                tbody.rows.push(row);
                tbody.rowStyles.push([
                    [['width:30', 'center']],
                    [['width:170', 'light', 'center']],
                    [['width:75', 'light', 'center']],
                    [['width:105', 'light', 'center']],
                    [['width:105', 'light', 'center']],
                    [['width:105', 'light', 'center']],
                ]);
            }
        });
        this.sumKosten = sumKosten;
        this.sumVerbrauch = sumVerbrauch;

        return tbody;
    }

    getTable(): any {
        const tbody: any = this.getTableBody();
        const tfoot: any = this.getTableFooter();
        const thead: any = this.getTableHeader();

        return {
            rows: [...thead.rows, ...tbody.rows, ...tfoot.rows],
            rowStyles: [...thead.rowStyles, ...tbody.rowStyles, ...tfoot.rowStyles],
        };
    }

    getTableBody(): any {
        return this.getDataRows();
    }

    getTableFooter(): any {
        const thead: any = { rows: [], rowStyles: [] };
        thead.rows.push([
            null,
            'Gesamt',
            null,
            this.sumVerbrauch !== null ? fn2nZ(this.sumVerbrauch) : null,
            this.sumKosten !== null ? fn2nZ(this.sumKosten) : null,
            null,
        ]);
        thead.rowStyles.push([
            [['width:30', 'center']],
            [['width:170', 'dark', 'bold', 'center']],
            ['width:75'],
            [['width:105', 'dark', 'bold', 'center']],
            [['width:105', 'dark', 'bold', 'center']],
            ['width:105'],
        ]);

        return thead;
    }

    getTableHeader(): any {
        const thead: any = { rows: [], rowStyles: [] };
        // #0
        thead.rows.push([
            <div style={{ paddingRight: 30, display: 'inline-flex', fontWeight: 600}}>
                Ausgangsdaten{this.renderHelp()}
            </div>,
        ]);
        thead.rowStyles.push([
            [['width:589', 'title'], { paddingLeft: 10, borderTopLeftRadius: '0pt', borderTopRightRadius: '0pt' }],
        ]);
        // #1
        thead.rows.push([
            'Verbrauch & Kosten',
        ]);
        thead.rowStyles.push([
            [['width:589', 'head', 'larger', 'bold']],
        ]);
        // #2
        thead.rows.push([
            'Energieträger',
            'Basisjahr',
            'Verbrauch\n[kWh]',
            'Kosten\n[€]',
            'Energiepreis\n[€/kWh]',
        ]);
        thead.rowStyles.push([
            [['width:200', 'head', 'top', 'bold']],
            [['width:75', 'head', 'top', 'center', 'bold']],
            [['width:105', 'head', 'center', 'bold']],
            [['width:105', 'head', 'center', 'bold']],
            [['width:105', 'head', 'center', 'bold']],
        ]);

        return thead;
    }

    onChange(event: any, key: string): void {
        const energietraeger: any = (getEnergietraegerKeyMap() || {})[key] || {};
        const jahr: number = getBasisjahr(energietraeger, this.props.data);
        const selection: any = { ...this.props.selection || {} };
        selection[key] = {
            checked: event.target.checked,
            jahr,
        };
        this.props.onChange(selection);
    }

    render(): JSX.Element {
        return renderTable(this.getTable());
    }

    renderEnergietraeger(key: string): JSX.Element {
        const energietraeger: any = (getEnergietraegerKeyMap() || {})[key] || {};
        const name: string = energietraeger.name2 || energietraeger.name || null;

        return (
            <label htmlFor={`input-selection-${key}`}>
                {name}
            </label>
        );
    }

    renderHelp(): JSX.Element {
        return (
            <WHelpIcon
                data={this.props.data}
                styleDivProps={{ marginLeft: 5, display: 'flex', alignSelf: 'center' }}
                styleProps={{ margin: 0 }}
                label='Energiekosten-Projektion - Ausgangsdaten'
                helpKey='energiekosten_projektion_ausgangsdaten'
                type='theme_info'
                style='theme_small'
                customElement='Info'
                editBar
                useTheme
            />
        );
    }

    renderSelection(key: string): JSX.Element {
        const disabled: boolean = this.props.disabled;
        const checked: boolean = ((this.props.selection || {})[key] || {}).checked || false;

        return (
            <input
                id={`input-selection-${key}`}
                type='checkbox'
                checked={checked}
                disabled={disabled}
                onChange={(event: any) => this.onChange(event, key)}
            />
        );
    }
}
